/**
 * Log Viewer 2 - Type Definitions
 *
 * Sequence-based log streaming with proper state management
 */

import type { LogErrorLink } from './parsers/applog-parser';
import { channelDef } from './channels';

export type { LogErrorLink };

// ============================================================================
// Parser Types
// ============================================================================

// One value per custom parser we have. journal/eventlog records are normally
// auto-routed by their buffer source, but they are also selectable here so a job
// can pin them as its default (e.g. a systemd job defaults to 'journal'). The
// stored/wire tokens are stable (renaming them would break &parser= links and
// saved job.extra_data.logParser values).
export type LogParserType = 'structured' | 'applog' | 'plain-text' | 'journal' | 'eventlog';

// Human-facing labels for the parsers — display only. Reuse this map anywhere a
// parser is shown to a user (the picker, the per-job select). Order here is the
// order shown in lists.
export const LOG_PARSER_LABELS: Record<LogParserType, string> = {
  'structured': 'Log4j / Accur8',
  // The applog channel carries STRUCTURED records (continuum_rpc.LogRecord): level,
  // caller, thread and the exception are fields on the wire, not substrings of a
  // rendered line. So this "parser" does no parsing — it is a field mapping. Note a
  // buffer that declares BufferSource.applog is routed here regardless of this
  // setting (see LogParsingPipeline): a structured record must never be regex-parsed.
  'applog': 'applog (structured)',
  'plain-text': 'Plain text (raw lines)',
  'journal': 'Journal (journalctl)',
  'eventlog': 'Windows Event Log',
};

// Parse a stored/URL parser token. Derived from LOG_PARSER_LABELS, so a parser
// added there is accepted here automatically — the previous hand-written if/else
// chain silently dropped 'applog' (the ?parser= link and the picker's own
// round-trip through the URL both snapped back to 'structured').
// 'accur8' is the legacy token for 'structured'. Unknown/absent -> undefined, so
// the caller can fall back (channel default, then 'structured').
export function parseLogParserType(token: string | null | undefined): LogParserType | undefined {
  if (!token) return undefined;
  if (token === 'accur8') return 'structured';
  return (token in LOG_PARSER_LABELS) ? (token as LogParserType) : undefined;
}

// Resolve the parser for a view. Most-specific wins:
//   1. explicit (?parser= in the URL, or the picker)
//   2. the job's configured default (job.extra_data.logParser)
//   3. the channel's default
// ...but a request is only honoured if the CHANNEL admits that parser (see
// channels.ts — the one table). Asking for a parser the channel can't use isn't a
// preference to be silently ignored; it's answered with what will actually render.
// That is the ?parser=journal-on-applog bug: the picker showed "journal" while
// applog did the rendering, because nothing validated the request against the
// channel.
export function resolveParserType(opts: {
  explicit?: LogParserType;
  jobDefault?: LogParserType;
  channel?: string;
}): LogParserType {
  const def = channelDef(opts.channel);
  const allowed = (p: LogParserType | undefined): LogParserType | undefined =>
    p !== undefined && def.parsers.includes(p) ? p : undefined;

  return allowed(opts.explicit) ?? allowed(opts.jobDefault) ?? def.defaultParser;
}

/** Controls which log source(s) the viewer uses */
export type LogSourceMode = 'auto' | 'archive-only' | 'nats-only';

// ============================================================================
// Stream Info & API Types
// ============================================================================

export interface StreamInfo {
  /** Current tip of the stream */
  maxSeq: number;
  /** Earliest available sequence (if retention/compaction exists) */
  minSeq?: number;
  /** Stream creation timestamp (milliseconds) */
  createdAt?: number;
  /** Total message count (for gap calculation) */
  messageCount?: number;
  /** Service label if available */
  serviceLabel?: string;
  /** Process start timestamp (milliseconds) */
  startedAt?: number;
  /** Process completion timestamp (milliseconds) */
  completedAt?: number;
  /** Channel sizes in bytes (stderr, stdout, etc.) */
  channelSizes?: Record<string, number>;
  /** Raw logarchive data from processRun JSONB (if available) */
  logarchive?: LogArchiveInfo;
}

export interface LogArchiveFileInfo {
  filename: string;
  firstSeq: number;
  lastSeq: number;
  messageCount: number;
  uncompressedBytes: number;
  compressedBytes: number;
  firstTimestamp: string;
  lastTimestamp: string;
  createdAt: string;
  checksum: string;
}

export interface LogArchiveChannelInfo {
  lastSeq: number;
  lastArchivedAt: string;
  files: LogArchiveFileInfo[];
}

export interface LogArchiveInfo {
  channels: Record<string, LogArchiveChannelInfo>;
  done: boolean;
}

export interface SubscribeOptions {
  processUid: string;
  channel: string;
  /** Start from this sequence (inclusive) */
  fromSeq: number;
  /** Optional: stop at this sequence (bounded mode) */
  toSeq?: number;
  /** Callback for message updates (cross-block continuations) */
  onUpdate?: (message: LogMessage) => void;
  /** Parser type to use for log parsing */
  parserType: LogParserType;
}

// ============================================================================
// Stream Range
// ============================================================================

export interface StreamRange {
  /** Start sequence (inclusive) */
  fromSeq: number;
  /** Optional end sequence (inclusive). When omitted, stream tails. */
  toSeq?: number;
}

export type WorkerState = "idle" | "consuming" | "paused" | "done" | "error";

// ============================================================================
// Message Types
// ============================================================================

export interface LogMessage {
  /** NATS sequence number (from SSE stream metadata) */
  natsSeq: number;
  /** StreamRecord.sequence (from proto field 3) */
  recordSeq: number;
  /** Line sequence - increments for each parsed line displayed in UI */
  lineSeq: number;
  /** Base date for relative timestamps */
  baseDate: number;
  /** Parsed message text (from parser) */
  text: string;
  /** Original unparsed line text */
  originalLine: string;
  /** Parsed timestamp (if available) */
  timestamp?: Date;
  /** Buffer source (stdout, stderr, etc) */
  source?: string;
  /** Parsed log level */
  level?: string;
  /** Parsed category */
  category?: string;
  /** Parsed thread name */
  thread?: string;
  /**
   * The process run whose OS process produced this record, when the record says so
   * (LogRecord.processRunUid): a checkpoint process's console is written by each
   * process run that runs a leg of it. Absent means the run the log belongs to.
   */
  processRunUid?: string;
  /** Flag indicating this is a continuation line (should not display timestamp, level, thread, category) */
  isContinuation?: boolean;
  /** Size in bytes of the SSE block this message came from */
  sseBlockSize?: number;

  // ---- applog only (structured records) ------------------------------------
  // These have no equivalent in a text-parsed line; they exist because the applog
  // channel carries the fields the writer used to destroy.

  /**
   * The exception, as a CHAIN, root LAST — message + concrete type + frames per link.
   * The root cause is the interesting end of it, and `type` being a field means "show
   * me every java.io.IOException" is a filter rather than a substring guess. Empty /
   * absent when the record carries no error.
   */
  errorChain?: LogErrorLink[];
  /**
   * > 0 when the WRITER's spool overflowed before this record (NATS was unreachable
   * long enough to fill it). Surfaced so the loss is VISIBLE — a truncated log that
   * admits what it dropped beats one that silently shows a hole.
   */
  droppedRecords?: number;
  /** The MDC / structured-logging bag (request ids, job uids, ...). */
  metadata?: Record<string, string>;
}

export type LogColumn = 'natsSeq' | 'recordSeq' | 'lineSeq' | 'source' | 'fullTimestamp' | 'timestamp' | 'level' | 'thread' | 'processRun' | 'category' | 'text' | 'raw' | 'isContinuation';

export interface ColumnConfig {
  column: LogColumn;
  label: string;
  defaultVisible: boolean;
  /** Width as a CSS value (e.g., "80px", "auto", "1fr") */
  width?: string;
}

// ============================================================================
// Stream Summary - Aggregated Metadata
// ============================================================================

export interface StreamSummary {

  /** Tracking sets for aggregation */
  categoriesSet: Set<string>;
  threadNamesSet: Set<string>;
  streamNamesSet: Set<string>;
  logLevelsSet: Set<string>;

  /** Temporal bounds */
  minTimestamp: number | null;
  maxTimestamp: number | null;

  maxNatsSeq: number | null;
  minNatsSeq: number | null;

  seenSequences: Set<number>;

  /** Volume metrics */
  linesProcessed: number;
  recordsProcessed: number;
  bytesProcessed: number;
  blocksProcessed: number;
  lineCounter: number;

  totalBytes: number;
  totalBlocks: number;

  /** Stream info from server */
  streamInfo: StreamInfo | null;

  /** Dirty flag - set to true when summary is modified, reset when sorted arrays are recomputed */
  // dirty: boolean;
}


// ============================================================================
// Worker Messages
// ============================================================================

export type WorkerCommand =
  | { type: "play"; config: PlayConfig }
  | { type: "resume" }
  | { type: "pause" }
  // Main thread's answer to an archive_rpc_request event (the worker cannot
  // own the authenticated WS client, so archive.v1 control calls relay
  // through the main thread; bytes still fetch directly in the worker).
  | { type: "archive_rpc_response"; id: number; response?: unknown; error?: string };

export interface PlayConfig {
  processUid: string;
  channel: string;
  range: StreamRange;
  authToken: string;
  hermesUrl: string;
  parserType: LogParserType;
  sourceMode: LogSourceMode;
}

export interface ArchiveProgress {
  phase: 'listing' | 'downloading' | 'decompressing' | 'parsing' | 'done';
  /** Current archive index (0-based) */
  archiveIndex: number;
  /** Total archives to process */
  archiveCount: number;
  /** Bytes downloaded so far (compressed) */
  bytesDownloaded: number;
  /** Total compressed bytes to download */
  bytesTotal: number;
  /** Total messages emitted from archives so far */
  messagesEmitted: number;
}

export type WorkerEvent =
  | { type: "state_changed"; workerState: WorkerState }
  | { type: "message"; message: LogMessage }
  | { type: "messages_batch"; messages: LogMessage[] }
  | { type: "message_update"; lineSeq: number; updatedMessage: LogMessage }
  | { type: "message_updates_batch"; updates: Array<LogMessage> }
  | { type: "status_update"; status: StreamViewStatusUpdate }
  | { type: "archive_progress"; progress: ArchiveProgress }
  | { type: "no_logs"; channel: string }
  | { type: "error"; error: string }
  // Worker → main thread: run an archive.v1 control RPC on the user's
  // authenticated WS session and answer with archive_rpc_response.
  | { type: "archive_rpc_request"; id: number; endPoint: string; request: Record<string, unknown> };

export interface StreamViewStatusUpdate {
  workerState: WorkerState;
  range: StreamRange;
  latestMaxSeq?: number;
  lastSeenSeq?: number;
  lag?: number;
  isLive: boolean;
  streamInfo?: StreamInfo;
  summary: StreamSummary;
}

// ============================================================================
// UI State
// ============================================================================

// (UI helper types removed; presets are resolved client-side before reaching the worker.)
